import type { LunarEvent, Source } from "../types";
import { formatEventDate, TYPE_LABELS } from "./format";
import {
  CONTACT_EMAIL,
  ORG_NAME,
  ORG_ORIGIN,
  SITE_DESCRIPTION,
  SITE_NAME,
  SITE_ORIGIN,
} from "./site";

const ORG_ID = `${ORG_ORIGIN}/#organization`;
const SITE_ID = `${SITE_ORIGIN}/#website`;
const CATALOG_ID = `${SITE_ORIGIN}/#datacatalog`;
const DATASET_ID = `${SITE_ORIGIN}/#dataset`;

export function siteGraph() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": ORG_ID,
        name: ORG_NAME,
        url: ORG_ORIGIN,
        email: CONTACT_EMAIL,
        address: {
          "@type": "PostalAddress",
          addressLocality: "Richardson",
          addressRegion: "TX",
          addressCountry: "US",
        },
      },
      {
        "@type": "WebSite",
        "@id": SITE_ID,
        name: SITE_NAME,
        url: SITE_ORIGIN,
        description: SITE_DESCRIPTION,
        inLanguage: "en",
        publisher: { "@id": ORG_ID },
        isPartOf: { "@id": ORG_ID },
      },
      {
        "@type": "DataCatalog",
        "@id": CATALOG_ID,
        name: `${SITE_NAME} lunar anomaly archive`,
        url: `${SITE_ORIGIN}/catalog`,
        description: SITE_DESCRIPTION,
        creator: { "@id": ORG_ID },
        publisher: { "@id": ORG_ID },
        isAccessibleForFree: true,
        inLanguage: "en",
        keywords: [
          "Transient Lunar Phenomena",
          "TLP",
          "Moon",
          "lunar anomalies",
          "Middlehurst",
          "Cameron",
        ],
        isPartOf: { "@id": SITE_ID },
      },
      {
        "@type": "Dataset",
        "@id": DATASET_ID,
        name: "Lunamalies catalogued Transient Lunar Phenomena",
        description:
          "Compiled records from Middlehurst et al. 1968 (NASA TR R-277), Cameron 1978 (NASA TM-79399), and Cameron 2006. Provisional historical observations, not confirmed impact flashes.",
        url: `${SITE_ORIGIN}/catalog`,
        creator: { "@id": ORG_ID },
        publisher: { "@id": ORG_ID },
        isAccessibleForFree: true,
        temporalCoverage: "0557/1995",
        spatialCoverage: { "@type": "Place", name: "Moon" },
        includedInDataCatalog: { "@id": CATALOG_ID },
        distribution: {
          "@type": "DataDownload",
          encodingFormat: "text/csv",
          contentUrl: `${SITE_ORIGIN}/api/export`,
        },
      },
    ],
  };
}

export function webPageLd(path: string, name: string) {
  const url = `${SITE_ORIGIN}${path === "/" ? "/" : path}`;
  const types: Record<string, string> = {
    "/about": "AboutPage",
    "/contact": "ContactPage",
    "/catalog": "CollectionPage",
  };
  return {
    "@context": "https://schema.org",
    "@type": types[path] ?? "WebPage",
    name,
    url,
    isPartOf: { "@id": SITE_ID },
    about: { "@id": DATASET_ID },
  };
}

function isoDate(event: LunarEvent): string | undefined {
  if (event.year == null) return undefined;
  const y = String(event.year).padStart(4, "0");
  if (event.month && event.day) {
    return `${y}-${String(event.month).padStart(2, "0")}-${String(event.day).padStart(2, "0")}`;
  }
  if (event.month) {
    return `${y}-${String(event.month).padStart(2, "0")}`;
  }
  return y;
}

export function eventLd(event: LunarEvent, source: Source | null) {
  const name = `${event.featureName ?? event.locationText} · ${formatEventDate(event)}`;
  const placeName =
    event.featureName ??
    (event.locationText && event.locationText !== "Unlocated"
      ? event.locationText
      : "Moon");
  const place: Record<string, unknown> = {
    "@type": "Place",
    name: `${placeName} (Moon)`,
  };
  if (event.latitude != null && event.longitude != null) {
    place.description = `Selenographic ${event.latitude >= 0 ? event.latitude : -event.latitude}°${event.latitude >= 0 ? "N" : "S"} ${event.longitude >= 0 ? event.longitude : -event.longitude}°${event.longitude >= 0 ? "E" : "W"}`;
  }
  const work: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": `${SITE_ORIGIN}/events/${event.id}`,
    name,
    description: event.description.replace(/\s+/g, " ").trim().slice(0, 400),
    url: `${SITE_ORIGIN}/events/${event.id}`,
    isPartOf: { "@id": DATASET_ID },
    about: place,
    keywords: ["Transient Lunar Phenomena", TYPE_LABELS[event.phenomenonTypeId]],
  };
  const date = isoDate(event);
  if (date) work.dateCreated = date;
  if (event.observer) work.contributor = event.observer;
  if (event.reference) work.citation = event.reference;
  if (source?.citation) {
    work.citation = [event.reference, source.citation].filter(Boolean);
    if (source.url) {
      work.isBasedOn = { "@type": "CreativeWork", name: source.title, url: source.url };
    }
  }
  return work;
}
